import React, { useEffect, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Mail, Loader2, ShieldCheck, Send } from "lucide-react";
import { C } from "./reportsUi";

const subjectFor = (label) => `MovingMyMama Reporting Center — ${label} — ${new Date().toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" })}`;

export default function EmailReportDialog({ open, onOpenChange, category, filters, builder, tabLabel }) {
  const [recipients, setRecipients] = useState([]);
  const [recipient, setRecipient] = useState("__me__");
  const [message, setMessage] = useState("");
  const [loadingR, setLoadingR] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoadingR(true);
    api.get("/reports/center/email-recipients")
      .then(({ data }) => setRecipients(data.recipients || []))
      .catch(() => toast.error("Could not load recipients."))
      .finally(() => setLoadingR(false));
  }, [open]);

  const send = async () => {
    setSending(true);
    try {
      const f = { ...(filters || {}) };
      if (category === "builder" && builder) f.report_type = builder.report_type;
      const body = { category, filters: f, message: message.trim() };
      if (recipient && recipient !== "__me__") body.recipient_user_id = recipient;
      const { data } = await api.post("/reports/center/email", body);
      toast.success(`${data.delivery_note} → ${data.masked_recipient}`);
      onOpenChange(false); setMessage("");
    } catch (e) { toast.error(errMsg(e, "Could not email the report.")); }
    finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="rc-email-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Mail size={18} style={{ color: C.navy }} /> Email this report</DialogTitle></DialogHeader>
        <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
          <div className="space-y-1.5">
            <Label>Recipient</Label>
            <Select value={recipient} onValueChange={setRecipient}>
              <SelectTrigger data-testid="rc-email-recipient">{loadingR ? <span className="text-[13px]">Loading…</span> : <SelectValue />}</SelectTrigger>
              <SelectContent>
                <SelectItem value="__me__">Email to myself</SelectItem>
                {recipients.filter((r) => !r.is_self).map((r) => <SelectItem key={r.id} value={r.id}>{r.name} · {r.email_masked}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-[11.5px]" style={{ color: C.slate }}>Only active internal team members in your scope can be selected. Clients, partners, and guests can never receive reports.</p>
          </div>
          <div className="space-y-1.5">
            <Label>Subject</Label>
            <Input data-testid="rc-email-subject" value={subjectFor(tabLabel)} readOnly className="bg-[#F4F8FB] text-[13px]" />
          </div>
          <div className="space-y-1.5">
            <Label>Short message (optional)</Label>
            <Textarea data-testid="rc-email-message" value={message} onChange={(e) => setMessage(e.target.value.slice(0, 500))} rows={2} placeholder="Add a short note for the team…" />
          </div>
          <div className="flex items-start gap-2 rounded-lg px-3 py-2 text-[12px]" style={{ backgroundColor: "rgba(47,125,90,0.10)", color: C.green }}>
            <ShieldCheck size={15} className="mt-0.5 shrink-0" />
            <span>Aggregate-first report — no client, lead, or private financial data. Uses the active filters ({tabLabel}).</span>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="rc-email-send" disabled={sending} onClick={send} className="gap-1.5 bg-[#9C1E22] hover:bg-[#861A1D] text-white">
            {sending ? <Loader2 className="animate-spin" size={15} /> : <Send size={15} />} Send report
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
